import {Injectable} from '@angular/core';

export type RichTextPastePolicy = 'balanced' | 'plain' | 'permissive';
export type RichTextLinkPolicy = 'strip' | 'safe-http' | 'none';

@Injectable({
  providedIn: 'root'
})
export class RichTextSanitizerService {
  private readonly blockedTags: Set<string> = new Set<string>(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math']);

  public sanitizeRichHtml(inputHtml: string, policy: RichTextPastePolicy = 'balanced', linkPolicy: RichTextLinkPolicy = 'strip'): string {
    if (!inputHtml) {
      return '';
    }

    if (policy === 'plain') {
      return this.escapeHtml(this.toPlainText(inputHtml));
    }

    const container = document.createElement('div');
    container.innerHTML = inputHtml;

    const out = document.createElement('div');
    const allowedTags = this.getAllowedTags(policy, linkPolicy);

    Array.from(container.childNodes).forEach((node) => {
      this.appendSanitizedNode(node, out, allowedTags, policy, linkPolicy);
    });

    return out.innerHTML;
  }

  public toPlainText(input: string): string {
    if (!input) {
      return '';
    }

    const container = document.createElement('div');
    container.innerHTML = input;
    return this.extractTextWithBreaks(container).trim();
  }

  public truncateHtmlByTextLength(sanitizedHtml: string, maxTextLength: number): string {
    if (!sanitizedHtml || maxTextLength <= 0) {
      return '';
    }

    const source = document.createElement('div');
    source.innerHTML = sanitizedHtml;

    const out = document.createElement('div');
    let remaining = maxTextLength;

    const cloneLimited = (node: Node): Node | null => {
      if (remaining <= 0) {
        return null;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (!text) {
          return null;
        }
        if (text.length <= remaining) {
          remaining -= text.length;
          return document.createTextNode(text);
        }
        const slice = text.slice(0, remaining);
        remaining = 0;
        return document.createTextNode(slice);
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
      }

      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();

      if (tag === 'br') {
        remaining -= 1;
        if (remaining < 0) {
          return null;
        }
        return document.createElement('br');
      }

      const clone = document.createElement(tag);
      if (el.hasAttribute('style')) {
        clone.setAttribute('style', el.getAttribute('style') ?? '');
      }
      if (tag === 'a' && el.hasAttribute('href')) {
        clone.setAttribute('href', el.getAttribute('href') ?? '');
      }

      for (const child of Array.from(el.childNodes)) {
        const childClone = cloneLimited(child);
        if (!childClone) {
          if (remaining <= 0) {
            break;
          }
          continue;
        }
        clone.appendChild(childClone);
      }

      if (clone.childNodes.length === 0 && !this.isSelfClosingTag(tag)) {
        return null;
      }

      return clone;
    };

    Array.from(source.childNodes).forEach((node) => {
      const cloned = cloneLimited(node);
      if (cloned) {
        out.appendChild(cloned);
      }
    });

    return out.innerHTML;
  }

  private appendSanitizedNode(
    sourceNode: Node,
    targetParent: HTMLElement,
    allowedTags: Set<string>,
    policy: RichTextPastePolicy,
    linkPolicy: RichTextLinkPolicy
  ): void {
    if (sourceNode.nodeType === Node.TEXT_NODE) {
      const text = sourceNode.textContent ?? '';
      if (text) {
        targetParent.appendChild(document.createTextNode(text));
      }
      return;
    }

    if (sourceNode.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const sourceElement = sourceNode as HTMLElement;
    const sourceTag = sourceElement.tagName.toLowerCase();
    const tag = this.normalizeTag(sourceTag);

    if (this.blockedTags.has(sourceTag)) {
      return;
    }

    if (tag === 'a') {
      if (linkPolicy === 'none') {
        return;
      }

      if (linkPolicy === 'strip') {
        Array.from(sourceElement.childNodes).forEach((child) => {
          this.appendSanitizedNode(child, targetParent, allowedTags, policy, linkPolicy);
        });
        return;
      }

      if (linkPolicy === 'safe-http') {
        const href = sourceElement.getAttribute('href') ?? '';
        if (!this.isSafeLink(href)) {
          Array.from(sourceElement.childNodes).forEach((child) => {
            this.appendSanitizedNode(child, targetParent, allowedTags, policy, 'strip');
          });
          return;
        }

        const safeAnchor = document.createElement('a');
        safeAnchor.setAttribute('href', href);
        safeAnchor.setAttribute('rel', 'noopener noreferrer');
        safeAnchor.setAttribute('target', '_blank');
        Array.from(sourceElement.childNodes).forEach((child) => {
          this.appendSanitizedNode(child, safeAnchor, allowedTags, policy, linkPolicy);
        });
        if (safeAnchor.childNodes.length > 0) {
          targetParent.appendChild(safeAnchor);
        }
        return;
      }
    }

    if (!allowedTags.has(tag)) {
      Array.from(sourceElement.childNodes).forEach((child) => {
        this.appendSanitizedNode(child, targetParent, allowedTags, policy, linkPolicy);
      });
      return;
    }

    const outputElement = document.createElement(tag);
    const safeStyle = this.getSafeStyle(sourceElement.getAttribute('style'));
    const normalizedStyle = this.normalizeStyleForLegacyTag(sourceTag, safeStyle);
    if (normalizedStyle) {
      outputElement.setAttribute('style', normalizedStyle);
    }

    Array.from(sourceElement.childNodes).forEach((child) => {
      this.appendSanitizedNode(child, outputElement, allowedTags, policy, linkPolicy);
    });

    if (outputElement.childNodes.length > 0 || this.isSelfClosingTag(tag)) {
      targetParent.appendChild(outputElement);
    }
  }

  private getAllowedTags(policy: RichTextPastePolicy, linkPolicy: RichTextLinkPolicy): Set<string> {
    if (policy === 'permissive') {
      const permissive = new Set(['p', 'strong', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ol', 'ul', 'li', 'br', 'span', 'blockquote', 'code', 'pre']);
      if (linkPolicy === 'safe-http') {
        permissive.add('a');
      }
      return permissive;
    }

    const balanced = new Set(['p', 'strong', 'em', 'h5', 'ol', 'ul', 'li', 'br', 'span']);
    if (linkPolicy === 'safe-http') {
      balanced.add('a');
    }
    return balanced;
  }

  private getSafeStyle(styleText: string | null): string {
    if (!styleText) {
      return '';
    }

    const allowed = new Set(['text-align', 'text-decoration', 'font-weight', 'font-style']);

    const parts = styleText
      .split(';')
      .map((entry) => entry.trim())
      .filter((entry) => !!entry);

    const safeParts: string[] = [];
    parts.forEach((part) => {
      const splitIndex = part.indexOf(':');
      if (splitIndex <= 0) {
        return;
      }
      const key = part.slice(0, splitIndex).trim().toLowerCase();
      const value = part.slice(splitIndex + 1).trim().toLowerCase();

      if (!allowed.has(key)) {
        return;
      }

      if (key === 'text-align' && ['left', 'center', 'right', 'justify', 'start', 'end'].includes(value)) {
        safeParts.push(`text-align:${value}`);
      }

      if (key === 'text-decoration' && value === 'underline') {
        safeParts.push('text-decoration:underline');
      }

      if (key === 'font-weight' && ['bold', '700', '600', '500'].includes(value)) {
        safeParts.push(`font-weight:${value}`);
      }

      if (key === 'font-style' && value === 'italic') {
        safeParts.push('font-style:italic');
      }
    });

    return safeParts.join(';');
  }

  private isSafeLink(href: string): boolean {
    if (!href) {
      return false;
    }

    const normalized = href.trim().toLowerCase();
    return normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('mailto:');
  }

  private isSelfClosingTag(tag: string): boolean {
    return tag === 'br';
  }

  private normalizeTag(sourceTag: string): string {
    if (sourceTag === 'b') {
      return 'strong';
    }
    if (sourceTag === 'i') {
      return 'em';
    }
    if (sourceTag === 'u') {
      return 'span';
    }
    return sourceTag;
  }

  private normalizeStyleForLegacyTag(sourceTag: string, safeStyle: string): string {
    if (sourceTag !== 'u') {
      return safeStyle;
    }

    if (!safeStyle) {
      return 'text-decoration:underline';
    }

    if (!safeStyle.includes('text-decoration:underline')) {
      return `${safeStyle};text-decoration:underline`;
    }

    return safeStyle;
  }

  private extractTextWithBreaks(root: HTMLElement): string {
    const lines: string[] = [];

    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        lines.push(node.textContent ?? '');
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();

      if (tag === 'br') {
        lines.push('\n');
        return;
      }

      if (tag === 'li') {
        lines.push('- ');
      }

      Array.from(element.childNodes).forEach((child) => walk(child));

      if (['p', 'div', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre'].includes(tag)) {
        lines.push('\n');
      }
    };

    Array.from(root.childNodes).forEach((child) => walk(child));

    return lines.join('').replace(/\n{3,}/g, '\n\n');
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
